import type { ICustomMessage } from '@/components/chat';
import { getRelayTargetLabel } from '@/hooks/chat/useMessageRelay';
import type { MessageRelayTargetV2 } from '@/services/getMessageRelayTargetsV2';

export type MessageLongPressAction = {
  kind: 'copy' | 'relay';
  label: string;
  run: () => void;
};

/**
 * What long-pressing a message offers: Copy for a message with text, then one
 * Relay per thread on the other side of an Ask Anonymously case.
 *
 * Relay is left out where the server would always refuse it: a message with no
 * `messageId` has not reached the server yet, a draft can still be retracted,
 * and a relayed copy's original is on the other side already. Files and
 * references go with the relay; the server reads them off the stored message,
 * so nothing here inspects them.
 */
export function buildMessageLongPressActions({
  message,
  relayTargets,
  onCopy,
  onRelay,
}: {
  message: Pick<ICustomMessage, 'text' | 'messageId' | 'draft' | 'relayedFromMessageId'>;
  relayTargets: readonly MessageRelayTargetV2[];
  onCopy: (text: string) => void;
  onRelay: (messageSid: string, target: MessageRelayTargetV2) => void;
}): MessageLongPressAction[] {
  const actions: MessageLongPressAction[] = [];

  const { text, messageId, draft, relayedFromMessageId } = message;

  if (text) {
    actions.push({ kind: 'copy', label: 'Copy', run: () => onCopy(text) });
  }

  if (messageId && !draft && !relayedFromMessageId) {
    for (const target of relayTargets) {
      actions.push({
        kind: 'relay',
        label: `Relay to ${getRelayTargetLabel(target)}`,
        run: () => onRelay(messageId, target),
      });
    }
  }

  return actions;
}
